import { useState } from "react";
import { Archive, ArchiveRestore, FolderInput, Send, Trash2, Undo2, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { TreeSelect } from "../tree-select/TreeSelect";
import { collectionToOption } from "../tree-select/treeOptions";
import { useArticleCollections } from "../../hooks/useArticleCollections";
import {
  useBulkDeleteArticles,
  useBulkUpdateArticleStatus,
  useMoveArticlesToCollection,
} from "../../hooks/useArticles";
import { ArticleStatus, type ArticleDto } from "../../types/articles";

interface ArticleBulkActionsBarProps {
  // The selected articles (all on the page being shown)
  selected: ArticleDto[];
  // Organizations where the user is an Admin - permanent delete is theirs
  adminOrganizationIds: string[];
  onDone: () => void;
  onClear: () => void;
}

type Confirming = "archive" | "delete" | null;

/**
 * Actions on the selected articles. Each sends only the articles it applies to (publishing the drafts, unpublishing
 * the published ones, ...), with the count on the button, so a mixed selection does what the buttons say.
 */
export function ArticleBulkActionsBar({ selected, adminOrganizationIds, onDone, onClear }: ArticleBulkActionsBarProps) {
  const bulkStatus = useBulkUpdateArticleStatus();
  const bulkDelete = useBulkDeleteArticles();
  const moveToCollection = useMoveArticlesToCollection();
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  // undefined until a target is picked, so Move can't file everything under "No collection" by accident
  const [targetCollectionId, setTargetCollectionId] = useState<string | null | undefined>(undefined);

  const drafts = selected.filter((a) => a.status === ArticleStatus.Draft);
  const published = selected.filter((a) => a.status === ArticleStatus.Published);
  const archived = selected.filter((a) => a.status === ArticleStatus.Archived);
  const notArchived = selected.filter((a) => a.status !== ArticleStatus.Archived);
  const deletable = archived.filter((a) => adminOrganizationIds.includes(a.organizationId));

  // Collections belong to one organization, so moving needs the selection to be in one
  const organizationIds = [...new Set(selected.map((a) => a.organizationId))];
  const moveOrganizationId = organizationIds.length === 1 ? organizationIds[0] : undefined;
  const { data: collections = [] } = useArticleCollections(moveOpen ? moveOrganizationId : undefined);

  const busy = bulkStatus.isPending || bulkDelete.isPending || moveToCollection.isPending;

  const setStatus = async (articles: ArticleDto[], status: ArticleStatus) => {
    try {
      await bulkStatus.mutateAsync({ articleIds: articles.map((a) => a.id), status });
      onDone();
    } catch {
      // Shown by the mutation hook
    }
  };

  const handleDelete = async () => {
    try {
      await bulkDelete.mutateAsync({ articleIds: deletable.map((a) => a.id) });
      onDone();
    } catch {
      // Shown by the mutation hook
    }
  };

  const handleMove = async () => {
    try {
      if (targetCollectionId === undefined) return;
      await moveToCollection.mutateAsync({ articleIds: selected.map((a) => a.id), collectionId: targetCollectionId });
      setMoveOpen(false);
      onDone();
    } catch {
      // Shown by the mutation hook
    }
  };

  return (
    <div
      role="toolbar"
      aria-label="Actions on selected articles"
      className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2"
    >
      <span className="text-sm font-medium text-foreground mr-1">
        {selected.length} selected
      </span>
      {drafts.length > 0 && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(drafts, ArticleStatus.Published)}>
          <Send className="h-4 w-4 mr-1" aria-hidden="true" />
          Publish ({drafts.length})
        </Button>
      )}
      {published.length > 0 && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(published, ArticleStatus.Draft)}>
          <Undo2 className="h-4 w-4 mr-1" aria-hidden="true" />
          Unpublish ({published.length})
        </Button>
      )}
      {notArchived.length > 0 && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirming("archive")}>
          <Archive className="h-4 w-4 mr-1" aria-hidden="true" />
          Archive ({notArchived.length})
        </Button>
      )}
      {archived.length > 0 && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(archived, ArticleStatus.Draft)}>
          <ArchiveRestore className="h-4 w-4 mr-1" aria-hidden="true" />
          Restore ({archived.length})
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={busy || !moveOrganizationId}
        title={moveOrganizationId ? undefined : "Select articles from one organization to move them - collections belong to an organization"}
        onClick={() => {
          setTargetCollectionId(undefined);
          setMoveOpen(true);
        }}
      >
        <FolderInput className="h-4 w-4 mr-1" aria-hidden="true" />
        Move to Collection
      </Button>
      {deletable.length > 0 && (
        <Button size="sm" variant="outline" className="text-red-600" disabled={busy} onClick={() => setConfirming("delete")}>
          <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
          Delete ({deletable.length})
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onClear} className="ml-auto">
        <X className="h-4 w-4 mr-1" aria-hidden="true" />
        Clear
      </Button>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === "delete"
                ? `Permanently delete ${deletable.length} archived article${deletable.length === 1 ? "" : "s"}?`
                : `Archive ${notArchived.length} article${notArchived.length === 1 ? "" : "s"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === "delete"
                ? "They're deleted with their feedback, view and revision history, and removed from every ticket they're linked to. This can't be undone."
                : "They're taken off the knowledge base and hidden from article lists, search and suggestions. Tickets keep their links, and you can restore them to drafts at any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirming === "delete" ? "bg-red-600 hover:bg-red-700" : undefined}
              onClick={() => (confirming === "delete" ? handleDelete() : setStatus(notArchived, ArticleStatus.Archived))}
            >
              {confirming === "delete" ? "Delete Permanently" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move {selected.length} article{selected.length === 1 ? "" : "s"}</DialogTitle>
            <DialogDescription>Choose the collection or section to file them under.</DialogDescription>
          </DialogHeader>
          <TreeSelect
            options={collections.map(collectionToOption)}
            value={targetCollectionId}
            onChange={setTargetCollectionId}
            placeholder="Choose a collection"
            noneLabel="No collection"
            searchPlaceholder="Search collections..."
            emptyMessage="No collections yet"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMove} disabled={targetCollectionId === undefined || moveToCollection.isPending}>
              {moveToCollection.isPending ? "Moving..." : "Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
